import { create } from '@bufbuild/protobuf'
import { useMutation } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'

import {
  getGetOngoingAssessmentQueryKey,
  getListOngoingAssessmentsQueryKey,
} from '@/api/assessments.hook'
import { useListUsers } from '@/api/list-users.hook'
import { User } from '@/gen/customer/management/v1/service_pb'
import { addOngoingAssessmentAssignee } from '@/gen/inventory/v1/assessment_service-AssessmentService_connectquery'
import {
  AddOngoingAssessmentAssigneeRequest,
  AddOngoingAssessmentAssigneeRequestSchema,
  GetOngoingAssessmentResponse,
  ListOngoingAssessmentsResponse,
} from '@/gen/inventory/v1/assessment_service_pb'
import { OngoingAssessment } from '@/gen/inventory/v1/ongoing_assessment_pb'

export const useOptimisticAddOngoingAssessmentAssignee = (companyId?: string) => {
  const queryClient = useQueryClient()
  const queryKey = getListOngoingAssessmentsQueryKey(companyId)
  const queryKeyWithoutCompanyId = getListOngoingAssessmentsQueryKey()
  const { data: users } = useListUsers()

  const queryKeys = [queryKey, queryKeyWithoutCompanyId]

  return useMutation(addOngoingAssessmentAssignee, {
    onMutate: async (variables) => {
      const typedVariables = create(AddOngoingAssessmentAssigneeRequestSchema, variables)

      const getOngoingAssessmentKey = typedVariables.ongoingAssessmentId
        ? getGetOngoingAssessmentQueryKey(typedVariables.ongoingAssessmentId)
        : undefined

      const previousDataMap = new Map<unknown[], ListOngoingAssessmentsResponse>()
      let previousGetOngoingAssessmentData: GetOngoingAssessmentResponse | undefined

      for (const key of queryKeys) {
        await queryClient.cancelQueries({ queryKey: key })

        const previousData = queryClient.getQueryData<ListOngoingAssessmentsResponse>(key)

        // Only apply optimistic update if data exists in cache to avoid cache pollution
        if (previousData) {
          previousDataMap.set(key, previousData)

          // Verify user information is available before attempting optimistic addition
          const newAssignee = users?.find((user) => user.id === typedVariables.userId)
          if (newAssignee) {
            const optimisticData = updateOngoingAssessmentsData(
              previousData,
              typedVariables,
              newAssignee,
            )
            queryClient.setQueryData(key, optimisticData)
          }
        }
      }

      // Update specific assessment detail query if a key was determined
      if (getOngoingAssessmentKey) {
        await queryClient.cancelQueries({ queryKey: getOngoingAssessmentKey })

        previousGetOngoingAssessmentData =
          queryClient.getQueryData<GetOngoingAssessmentResponse>(getOngoingAssessmentKey)

        // Only update if the specific assessment data is currently cached
        if (previousGetOngoingAssessmentData) {
          const newAssignee = users?.find((user) => user.id === typedVariables.userId)
          if (newAssignee) {
            const optimisticGetData = applyNewDataOnOngoingAssessment(
              previousGetOngoingAssessmentData,
              typedVariables,
              newAssignee,
            )
            queryClient.setQueryData(getOngoingAssessmentKey, optimisticGetData)
          }
        }
      }

      return { previousDataMap, previousGetOngoingAssessmentData, getOngoingAssessmentKey }
    },
    onError: (_error, _variables, context) => {
      // Roll back list queries if they were optimistically updated
      if (context?.previousDataMap) {
        context.previousDataMap.forEach((previousData, key) => {
          queryClient.setQueryData(key, previousData)
        })
      }

      // Roll back detail query if it was optimistically updated
      if (context?.getOngoingAssessmentKey && context.previousGetOngoingAssessmentData) {
        queryClient.setQueryData(
          context.getOngoingAssessmentKey,
          context.previousGetOngoingAssessmentData,
        )
      }
    },
    onSettled: async (_data, _error, variables) => {
      const typedVariables = create(AddOngoingAssessmentAssigneeRequestSchema, variables)
      const getOngoingAssessmentKey = typedVariables.ongoingAssessmentId
        ? getGetOngoingAssessmentQueryKey(typedVariables.ongoingAssessmentId)
        : undefined

      await Promise.all([
        ...queryKeys.map((key) => queryClient.invalidateQueries({ queryKey: key })),
        ...(getOngoingAssessmentKey
          ? [queryClient.invalidateQueries({ queryKey: getOngoingAssessmentKey })]
          : []),
      ])
    },
  })
}

const updateOngoingAssessmentsData = (
  oldData: ListOngoingAssessmentsResponse,
  variables: AddOngoingAssessmentAssigneeRequest,
  newAssignee: User,
): ListOngoingAssessmentsResponse => {
  const { ongoingAssessmentId, userId } = variables

  const updatedOngoingAssessments = oldData.ongoingAssessments.map(
    (assessment: OngoingAssessment) => {
      // Only modify the assessment targeted
      if (assessment.id !== ongoingAssessmentId) {
        return assessment
      }

      // Prevent duplicate assignees in the UI if already present
      const assigneeAlreadyExists = assessment.assignees.some(
        (assignee: User) => assignee.id === userId,
      )

      if (assigneeAlreadyExists) {
        return assessment
      }

      // Add the new assignee and sort by name (or email if name is empty), case-insensitive
      const updatedAssignees = [...assessment.assignees, newAssignee].sort((a, b) => {
        const nameA = (a.name || a.email).toLowerCase()
        const nameB = (b.name || b.email).toLowerCase()
        return nameA.localeCompare(nameB)
      })

      return {
        ...assessment,
        assignees: updatedAssignees,
      }
    },
  )

  return {
    ...oldData,
    ongoingAssessments: updatedOngoingAssessments,
  }
}

/**
 * Optimistically updates a cached `GetOngoingAssessmentResponse` by adding an assignee to the
 * matching ongoing assessment.
 * Ensures `assignees` remain sorted by name (fallback to email), case-insensitive.
 */
const applyNewDataOnOngoingAssessment = (
  oldData: GetOngoingAssessmentResponse,
  variables: AddOngoingAssessmentAssigneeRequest,
  newAssignee: User,
): GetOngoingAssessmentResponse => {
  const assessment = oldData.ongoingAssessment
  // Safety check to ensure the cached assessment matches the targeted one
  if (!assessment || assessment.id !== variables.ongoingAssessmentId) {
    return oldData
  }

  // Prevent duplicate assignees in the detail view
  const assigneeAlreadyExists = assessment.assignees.some(
    (assignee: User) => assignee.id === variables.userId,
  )
  if (assigneeAlreadyExists) {
    return oldData
  }

  // Ensure the user object is found in the users list before updating

  const updatedAssignees = [...assessment.assignees, newAssignee].sort((a, b) => {
    const nameA = (a.name || a.email).toLowerCase()
    const nameB = (b.name || b.email).toLowerCase()
    return nameA.localeCompare(nameB)
  })

  return {
    ...oldData,
    ongoingAssessment: {
      ...assessment,
      assignees: updatedAssignees,
    },
  }
}
